import React, { useState, useEffect } from 'react';
import apiClient from '../utils/apiClient';
import Select from 'react-select';
import { SERVER_BASE } from '../config/server';
import { useTable, useSortBy, usePagination, useGlobalFilter } from 'react-table';
import * as XLSX from 'xlsx';
import 'bootstrap/dist/css/bootstrap.min.css';
import Swal from 'sweetalert2';

const ItemGroupType = () => {
  const [itemGroups, setItemGroups] = useState([]);
  const [selectedItemGroups, setSelectedItemGroups] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const code = localStorage.getItem('code');
  const token = localStorage.getItem('token');
  const types = [
    { value: "Asset", label: "Asset / Capital Nature" },
    { value: "Consumables", label: "Useable / Consumables" },
    { value: "Fixtures & Fittings", label: "Fixtures & Fittings" },
  ];

  useEffect(() => {
    fetchItemGroups();
  }, []);

  const fetchItemGroups = async () => {
    try {
      const response = await apiClient.get(SERVER_BASE + "/item-groups", {
        params: { code },
        headers: {
          Authorization: `Bearer ${token}`, // Fix template literal
        },
      });
      setItemGroups(
        response.data.map((group) => ({
          value: group.groupName,
          label: `${group.groupCode} - ${group.groupName}`,
          type: group.type,
          itemGroupCode: group.groupCode,
        }))
      );
    } catch (error) {
      console.error("Error fetching item groups:", error);
    }
  };

  const handleGetItems = async () => {
    if (!selectedItemGroups.length) {
      Swal.fire({
        icon: 'warning',
        title: 'Oops...',
        text: 'Please Select at least One Group!',
        position: 'top-end',
        showConfirmButton: false,
        timer: 1500,
        toast: true,
      });
      return;
    }
    if (!selectedTypes.length) {
      Swal.fire({
        icon: 'warning',
        title: 'Oops...',
        text: 'Please Select at least One Type!',
        position: 'top-end',
        showConfirmButton: false,
        timer: 1500,
        toast: true,
      });
      return;
    }

    setLoading(true);
    try {
      const groupCodes = selectedItemGroups.map((group) => group.itemGroupCode);
      const selectedTypeValues = selectedTypes.map((type) => type.value);

      const response = await apiClient.get(`${SERVER_BASE}/getItemsByGroupAndItem`, {
        params: { groupCodes, selectedTypeValues, code },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setItems(response.data);
    } catch (error) {
      console.error("Error fetching items:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportToExcel = () => {
    const exportData = items.map((item, index) => ({
      "S. No": index + 1,
      ...item,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData, { origin: 3 });
    XLSX.utils.sheet_add_aoa(
      worksheet,
      [[`Item Groups: ${selectedItemGroups.map((group) => group.label).join(", ")} `]],
      { origin: 0 }
    );
    XLSX.utils.sheet_add_aoa(
      worksheet,
      [[`Types: ${selectedTypes.map((type) => type.label).join(", ")} `]],
      { origin: 1 }
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Items");
    XLSX.writeFile(workbook, "Items.xlsx");
  };

  const columns = React.useMemo(
    () => [
      {
        Header: 'S. No',
        accessor: 'serialNumber',
        Cell: ({ row }) => row.index + 1, // Calculate serial number dynamically
      },
      { Header: 'Group Code', accessor: 'itemGroupCode' },
      { Header: 'Name', accessor: 'printName' },
      { Header: 'Type', accessor: 'type' },
      { Header: 'UOM', accessor: 'uom' },
      { Header: 'Avg Weight', accessor: 'avgWeight' },
      { Header: 'Additional Names', accessor: 'additionalNames' },
      { Header: 'Departments', accessor: 'concernedDepartments' },
      { Header: 'Description', accessor: 'itemDesc' },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = useTable(
    {
      columns,
      data: items,
      initialState: { pageIndex: 0, pageSize: 10 },
      manualPagination: true, // Allow manual pagination handling
      pageCount: Math.ceil(items.length / 10), // Dynamically calculate the page count
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  return (
    <div className="formcontainer mt-3">
      <h5>Item Group List</h5>
      <div className="mb-4">
        <div className="row align-items-end">
          <div className="col-md-4">
            <label>Select Item Group:</label>
            <Select
              options={itemGroups}
              isMulti
              onChange={setSelectedItemGroups}
              placeholder="Select Item Groups"
            />
          </div>
          <div className="col-md-4">
            <label>Select Type:</label>
            <Select
              options={types}
              isMulti
              onChange={setSelectedTypes}
              placeholder="Select Types"
            />
          </div>
          {items.length > 0 && (
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Search"
                value={globalFilter || ''}
                onChange={(e) => setGlobalFilter(e.target.value)}
              />
            </div>
          )}
        </div>
        <div className="row mt-2">
          <div className="col-md-4 d-flex align-items-center">
            <button
              onClick={handleGetItems}
              disabled={loading}
              className="btn btn-outline-success me-2"
            >
              {loading ? 'Loading...' : 'Get Items'}
            </button>
            {items.length > 0 && (
              <button
                className="btn btn-outline-secondary"
                onClick={handleExportToExcel}
                disabled={items.length === 0}
              >
                Export to Excel
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="table-responsive">
        <table
          style={{ fontSize: '15px', verticalAlign: 'baseline' }}
          className="table table-striped table-bordered"
          {...getTableProps()}
        >
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {column.render('Header')}
                    <span>
                      {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map(row => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map(cell => (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* <div className="d-flex justify-content-between align-items-center mt-3">
              <button
                className="btn btn-secondary me-2"
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              >
                Previous
              </button>
              <span>
                Page {pageIndex + 1} of {pageOptions.length}
              </span>
              <button
                className="btn btn-secondary"
                onClick={() => nextPage()}
                disabled={!canNextPage}
              >
                Next
              </button>
            </div> */}
      </div>
    </div>
  );
};

export default ItemGroupType;
